import { mutationKeyFields } from "common/types/mainMutationKeyFields";
import { type TypedTypePolicies } from "common/types/mainApolloTypePolicies";
import { type FieldFunctionOptions, type TypePolicies } from "@apollo/client";
import { relayStylePagination } from "@apollo/client/utilities";
import {
  type AssetObjectType,
  type ESignatureRequestRecipientObjectType,
  type UnifiedPortalActivationDetailsResponse,
  type PositionObjectType,
} from "common/types/mainTypes";

/**
 * Generic pagination caching policy to handle pagination. This tells the cache how to
 * handle fields that support limit/offset values.
 *
 * Adopted from Apollo's doc:
 * https://www.apollographql.com/docs/react/caching/cache-field-behavior/#handling-pagination
 * See AssetObjectTypeConnection in sharedTypePolicies for usage.
 */
function afterIdLimitPaginatedFieldPolicy<T>() {
  return {
    merge(existing: T[], incoming: T[], { args }: FieldFunctionOptions): T[] {
      const merged = existing ? existing.slice(0) : [];
      // Insert the incoming elements in the right places, according to args.
      const offset: number = args?.offset || 0;
      const end = offset + Math.min(args?.limit || Number.MAX_SAFE_INTEGER, incoming.length);
      for (let i = offset; i < end; ++i) {
        merged[i] = incoming[i - offset];
      }
      return merged;
    },
    read(existing: T[], { args }: FieldFunctionOptions) {
      // If we read the field before any data has been written to the
      // cache, this function will return undefined, which correctly
      // indicates that the field is missing.
      const offset: number = args?.offset || 0;
      const limit: number = args?.limit || 0;
      const page = existing && offset && limit && existing.slice(offset, offset + limit);
      // If we ask for a page outside the bounds of the existing array,
      // page.length will be 0, and we should return undefined instead of
      // the empty array.
      if (page && page.length > 0) {
        return page;
      }
      return existing;
    },
  };
}

/**
 * TypePolicies tells the cache how to interact with specific types in the schema.
 * Lets us provide  an object mapping of __typename strings to TypePolicy objects
 * when creating a new InMemoryCache object.
 *
 * The most common use case for adding a type policy is when working with an ObjectType
 * that doesn't have a clear ID field. These ObjectTypes are usually fields of an
 * existing ObjectType with an ID.
 *
 * When specifying merge=true, this will merge incoming
 * fields with existing fields while also recursivly calling any custom field's merge functions
 * as well. IE similar to {...existing, ...incoming}
 *
 * When specifying merge=false, this will always take the incoming data from the query.
 *
 *
 * Scenario for merge=true. We can safely merge fields for PositionObjectType.transactionTotals
 * In this scenario, TransactionTotalObject's identity is implied by the Position which means
 * TransactionTotalObject differing fields can be combined without losing data.
 * For example, given the 2 queries below with the same id, when specifying merge=true,
 * Apollo cache now understands that transactionTotals fields can be merged safely if
 * they come from the same PositionObjectType. This is an improvement over the default
 * behavior where PositionQuery2's TransactionTotalObject would entirely replace
 * PositionQuery1's.
 *
 * query PositionQuery1 (id=1) {
 *   PositionObjectType {
 *     id
 *     transactionTotals {
 *       amountCommitted
 *     }
 *   }
 * }
 * query PositionQuery2 (id=1) {
 *   PositionObjectType {
 *     id
 *     transactionTotals {
 *       ytdAmountCalled
 *     }
 *   }
 * }
 *
 * Apollo Docs:
 * TypePolicy API: https://www.apollographql.com/docs/react/caching/cache-configuration/#typepolicy-fields
 * Custom field behaviors: https://www.apollographql.com/docs/react/caching/cache-field-behavior/
 * Merging non-normalized objects: https://www.apollographql.com/docs/react/caching/cache-field-behavior/#merging-non-normalized-objects
 *
 */
export const sharedTypePolicies: TypedTypePolicies = {
  AccountObjectType: {
    fields: {
      assetsIndirect: {
        merge: true,
      },
      publishedAssetsIndirect: {
        merge: true,
      },
    },
  },
  ArenaObjectType: {
    fields: {
      features: {
        merge: true,
      },
    },
  },
  DevFeatures: {
    merge: true,
  },
  FeatureFlagType: {
    keyFields: ["name"],
  },
  PositionObjectType: {
    fields: {
      portalTransactionPermissions: {
        merge: true,
      },
      transactionTotals: {
        merge: true,
      },
      gpLiteTransactionTotals: {
        merge: true,
      },
    },
  },
  InvestorDiligenceObjectType: {
    fields: {
      investorDiligenceComments: {
        merge: true,
      },
    },
  },
  AssetObjectTypeConnection: {
    fields: {
      edges: afterIdLimitPaginatedFieldPolicy<AssetObjectType>(),
    },
  },
  PositionConnection: {
    fields: {
      edges: afterIdLimitPaginatedFieldPolicy<PositionObjectType>(),
    },
  },
  ESignatureRequestRecipientConnection: {
    fields: {
      edges: afterIdLimitPaginatedFieldPolicy<ESignatureRequestRecipientObjectType>(),
    },
  },
  GpArenaTwoFaNumbersObjectType: {
    keyFields: false as const,
  },
  UniqueIdentifiersObjectType: {
    keyFields: [],
  },
  AssetOwningPositionInfo: {
    keyFields: ["positionId"],
  },
  SubscriptionContactObjectType: {
    keyFields: ["contactUUID"],
  },
  SubscriptionV2ObjectType: {
    keyFields: ["id"],
  },
  CheckPaymentPrefObjectType: {
    keyFields: ["paymentPrefId"],
  },
  ACHPaymentPrefObjectType: {
    keyFields: ["paymentPrefId"],
  },
  WirePaymentPrefObjectType: {
    keyFields: ["paymentPrefId"],
  },
  NdaObjectType: {
    keyFields: ["id"],
  },
  DocumentFolder: {
    keyFields: ["id"],
    fields: {
      documents: {
        merge: true,
      },
      folders: {
        merge: true,
      },
    },
  },
  FolderDocument: {
    keyFields: ["id"],
  },
  Query: {
    fields: {
      logs: relayStylePagination(["ofType"]),
      usersCcTools: relayStylePagination(["filters"]),
      unifiedPortalActivationDetails: {
        merge(
          existing: UnifiedPortalActivationDetailsResponse,
          incoming: UnifiedPortalActivationDetailsResponse,
        ) {
          return {
            ...existing,
            ...incoming,
          };
        },
      },
      unifiedPortalAccounts: relayStylePagination(["filters", "sort"]),
      dataRoomByDataRoomKey: {
        merge: true,
      },
    },
  },
};

export const typePolicies: TypePolicies = {
  ...mutationKeyFields,
  ...sharedTypePolicies,
};
